import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

import useClassy from '@core/hooks/useClassy';
import useUniqueId from '@core/hooks/useUniqueId';
import { useSuperHubStore } from '@core/store';
import { isBranch } from '@core/store/SuperHub/util';

import { useMergeBranchModalContext } from '@routes/SuperHub/Versions/Modals';

import type { ButtonProps } from '@ui/Button';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import type { NotificationProps } from '@ui/Notification';
import Notification, { notify } from '@ui/Notification';

import styles from './index.module.scss';
import useEditorLayoutNotificationStyle from './useEditorLayoutNotificationStyle';

interface BranchSaveNotificationProps extends NotificationProps {
  isCheckingMergeStatus: ButtonProps['loading'];
  onClickMerge: ButtonProps['onClick'];
}

function BranchSaveNotification({ isCheckingMergeStatus, onClickMerge, ...rest }: BranchSaveNotificationProps) {
  const bem = useClassy(styles, 'BranchSaveNotification');

  return (
    <Notification theme="dark" {...rest}>
      <Flex align="end" gap="sm" layout="col">
        <Flex align="center" gap="sm" layout="row">
          <Icon color="color-text-minimum" name="check" />
          <Flex gap={3} layout="col">
            <div>Changes Saved</div>
            <div className={bem('-description')}>Your changes are not published until merged</div>
          </Flex>
        </Flex>
        <Button loading={isCheckingMergeStatus} onClick={onClickMerge} size="xs" uppercase={false}>
          Merge Now
        </Button>
      </Flex>
    </Notification>
  );
}

/**
 * @returns a function to `notify` that a branch save has been successful. The
 * triggered notification contains a CTA that allows the user to launch the
 * merge flow for the current branch.
 *
 * ⚠️ Consumers of this hook must wrap its content in a `BlockingModalProvider` and
 * `MergeBranchModalProvider`.
 */
export default function useBranchSaveNotification() {
  const uid = useUniqueId('useBranchSaveNotification');

  const style = useEditorLayoutNotificationStyle();
  const currentBranch = useSuperHubStore(s => s.currentBranch);

  const { setBranchToMerge, mergeStatus } = useMergeBranchModalContext();
  const getMergeStatus = useSuperHubStore(s => s.branches.getMergeStatus);
  const [isCheckingMergeStatus, setIsCheckingMergeStatus] = useState(false);

  const handleMerge = useCallback(async () => {
    if (isBranch(currentBranch)) {
      setIsCheckingMergeStatus(true);
      const { data } = await getMergeStatus(currentBranch.name);
      setBranchToMerge(currentBranch, data);
      setIsCheckingMergeStatus(false);
    }
  }, [currentBranch, getMergeStatus, setBranchToMerge]);

  useEffect(() => {
    // when we have the merge status and are about to open the merging modal,
    // dismiss the toast
    if (mergeStatus) {
      toast.dismiss();
    }
  }, [mergeStatus]);

  return useCallback(() => {
    notify(
      <BranchSaveNotification isCheckingMergeStatus={isCheckingMergeStatus} onClickMerge={handleMerge} style={style} />,
      {
        // provide a stable id so that we re-render the content of an existing
        // notification rather than firing off a duplicate notification. see
        // https://react-hot-toast.com/docs/toast#prevent-duplicate-toasts
        id: uid('branch-save-notification'),
      },
    );
  }, [handleMerge, isCheckingMergeStatus, style, uid]);
}
